// The AI place source for a whole trip (2026-10-16): the question that asks
// the AI to CHOOSE the places — day by day, with a time-of-day hint and a
// visit length each, plus 3 extras per city — and the call that returns them
// as entries. The schedule is built by the app (Common/aiDaySchedule.js).
// Shared by Build My Trip's "AI" source and Create Trip w/AI (TripAI.js).
//
// The question carries no scheduling rules (open days, filling the hours,
// per-city windows): those made the AI reason 3-4x longer (30-47 s vs 5-19 s
// in testing) and are applied by the scheduler with real opening hours.
// Instead it gets a day-by-day plan — where, which hours, about how many
// places (sized here from the day hours, meals and pace).

import { planWindows, rowsToEntries } from "./aiDaySchedule";
import { API_BASE_URL, describeAIResponseError } from "./Common";
import { totalDaySpan } from "./DestinationValidation";

const isCountryLevel = (d) => !d.city || d.city.trim().toLowerCase() === (d.country || "").trim().toLowerCase();

// The trip in words: destinations with dates, preferences, include/exclude.
// prefs = the guide's aiPreferences (TripType, travelingWith, selectedBudget,
// selectedPace, selectedTransportation, includeExcursions,
// selectedAccessibility, focusOn, Activities).
export function describeTrip({ destinations, prefs = {}, include = "", includeOnly = false, exclude = "" }) {
  const dests = destinations;
  const d0 = dests[0];
  const numDays = Math.max(1, totalDaySpan(dests));
  const word = (v) => (v ? ` ${v}` : "");
  const destLabel = (d) => (isCountryLevel(d) ? d.country : `${d.city}, ${d.country}`);
  const fmt = (dt) => `${dt.toFormat("LLL dd yyyy")} at ${dt.toFormat("HH:mm")}`;
  let base;
  if (dests.length === 1) {
    base = `${numDays} day${word(prefs.TripType)}${word(prefs.selectedBudget)}${word(prefs.selectedPace)}${word(prefs.travelingWith)} vacation${word(prefs.selectedTransportation)} in ${destLabel(d0)} from ${fmt(d0.dateStart)} to ${fmt(d0.dateEnd)}`;
    if (isCountryLevel(d0)) base += `. No specific city was given for this trip — choose specific cities/regions within ${d0.country} yourself (e.g. a sensible multi-city route) and use each attraction's actual city as its "City" field`;
  } else {
    const itinerary = dests.map((d, i) => `${i + 1}. ${destLabel(d)} from ${fmt(d.dateStart)} to ${fmt(d.dateEnd)}`).join("; ");
    base = `${numDays} day${word(prefs.TripType)}${word(prefs.selectedBudget)}${word(prefs.selectedPace)}${word(prefs.travelingWith)} multi-city vacation${word(prefs.selectedTransportation)} with the following destinations: ${itinerary}`;
    if (dests.some(isCountryLevel)) base += `. For any destination with no specific city given, choose specific cities/regions within that country yourself and use each attraction's actual city as its "City" field`;
  }
  if (prefs.includeExcursions) base += `. Also include worthwhile day-trip excursions to nearby towns/countryside, not just attractions within the destination city itself`;
  if (prefs.selectedAccessibility) base += `. This trip must be accessible for a traveler with ${prefs.selectedAccessibility === "wheelchair accessible" ? "wheelchair accessibility needs — avoid attractions or routes with stairs or no elevator/ramp access" : "limited mobility — avoid attractions or routes requiring extensive walking or stairs"}`;
  if (prefs.focusOn) base += `. Focus on ${prefs.focusOn}`;
  if (include) base += `. Include ${includeOnly ? "only" : ""} ${include}`;
  if (exclude) base += `. Exclude ${exclude}`;
  base += prefs.Activities ? `. Include the following activities ${prefs.Activities}.` : ".";
  return base;
}

// The whole question. `pace` may be the preference's words ("relaxed") or
// Build My Trip's visit-length multiplier (1.3 = relaxed) — see
// aiDaySchedule.planWindows.
export function buildPlacesQuestion({ destinations, prefs = {}, startH = 9, endH = 20, meals = {}, include = "", includeOnly = false, exclude = "", pace }) {
  const windows = planWindows(destinations, { startH, endH, pace: pace ?? prefs.selectedPace, meals });
  const where = (d) => (isCountryLevel(d) ? `${d.country} (cities of your choice — a sensible route, about 2-3 days per city)` : `${d.city}, ${d.country}`);
  const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  const byDay = new Map();
  windows.forEach((w) => { if (!byDay.has(w.day)) byDay.set(w.day, []); byDay.get(w.day).push(w); });
  const dayLines = [...byDay.entries()].map(([day, ws]) =>
    `Day ${day}: ` + ws.map((w) => `${where(destinations[w.destIdx])} ${hhmm(w.start)}-${hhmm(w.end)}, ${w.count ? `about ${w.count} attraction${w.count > 1 ? "s" : ""}` : "no attractions (arrival only)"}`).join("; then ")
  ).join(". ");
  const mealWords = ["breakfast", "lunch", "dinner"].filter((m) => meals[m]);
  const mealText = mealWords.length
    ? ` For each day, also add one restaurant for ${mealWords.join(" and one for ")} near that day's places, with time "${mealWords.join('" / "')}" (not counted in the attractions above).`
    : " Do not include restaurants or meals.";
  const timeWords = ['"morning"', '"afternoon"', '"sunset"', '"evening"', '"any"', ...mealWords.map((m) => `"${m}"`)].join(", ");
  return `Choose the attractions for a ${describeTrip({ destinations, prefs, include, includeOnly, exclude })} We build the schedule ourselves, so do not write times or a schedule — only choose the places, day by day: ${dayLines}.${mealText}` +
    ` Put attractions that are near each other on the same day. Each place only once — no repeats. After those, add 3 extra alternative attractions per city with day number 0. Use each place's real city.` +
    ` Reply with only an RFC8259 compliant JSON array of arrays, one per place, no keys and no other text: [["place name", "city", "country", "category", day number, time, visit minutes]] where time is one of ${timeWords} — use "any" unless the time of day really matters (a morning market, a sunset viewpoint, evening nightlife).`;
}

// The reply's text → entries (aiDaySchedule.rowsToEntries). Tolerates code
// fences, text around the JSON, and a reply cut off mid-row.
export function parsePlacesReply(text) {
  let s = String(text || "").replace(/```json\s*/gi, "").replace(/```\s*/g, "");
  const start = s.indexOf("[");
  if (start === -1) return [];
  s = s.substring(start);
  const tryParse = (t) => { try { return JSON.parse(t); } catch { return null; } };
  let rows = tryParse(s.substring(0, s.lastIndexOf("]") + 1));
  if (!Array.isArray(rows)) {
    const lastRow = s.lastIndexOf("],");
    rows = lastRow !== -1 ? tryParse(s.substring(0, lastRow + 1) + "]") : null;
  }
  if (!Array.isArray(rows)) return [];
  // Extras (day 0) capped at 3 per city — a verbose reply mustn't balloon
  // into extra place searches.
  const perCity = new Map();
  return rowsToEntries(rows).filter((e) => {
    if (!e.IsBackup) return true;
    const n = perCity.get(e.City) || 0;
    perCity.set(e.City, n + 1);
    return n < 3;
  });
}

// Asks the AI. Resolves { entries } or { error } (a message for the user).
export async function askForPlaces(question) {
  try {
    const r = await fetch(`${API_BASE_URL}/travel_questionsGPT5.6-luna.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ q: question }),
    });
    const response = await r.json();
    const text = response?.choices?.[0]?.message?.content;
    if (!text) return { error: describeAIResponseError(response) };
    const entries = parsePlacesReply(text);
    if (entries.length === 0) return { error: "The AI didn't return any places. Please try again." };
    return { entries };
  } catch (e) {
    return { error: "Couldn't reach the AI service. Check the connection and try again." };
  }
}
